import React from 'react';
import { X, File as FileIcon } from 'lucide-react';

interface FileUploadPreviewProps {
  files: File[];
  onRemoveFile: (fileName: string) => void;
  progressMap?: Record<string, number>;
}

const getFileType = (file: File) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
}

// 单个文件预览组件，处理 useEffect 逻辑
const FilePreviewItem: React.FC<{ file: File, onRemove: (name: string) => void, progress?: number }> = ({ file, onRemove, progress }) => {
    const fileType = getFileType(file);
    const [previewUrl, setPreviewUrl] = React.useState<string>('');

    React.useEffect(() => {
        if (fileType === 'image') {
            const reader = new FileReader();
            reader.onload = () => setPreviewUrl(String(reader.result || ''));
            reader.readAsDataURL(file);
        }
    }, [file, fileType]);

    return (
        <div className="relative w-20 h-20 flex-shrink-0 rounded-md overflow-hidden border border-border group bg-muted">
            {fileType === 'image' ? (
                <img src={previewUrl} alt={file.name} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-1">
                    <FileIcon className="w-8 h-8 text-muted-foreground" />
                    <p className="text-[10px] text-center truncate w-full px-1 mt-1">{file.name}</p>
                </div>
            )}
            <button
                onClick={() => onRemove(file.name)}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
                aria-label="Remove file"
            >
                <X className="w-3 h-3" />
            </button>
            {typeof progress === 'number' && (
                <div className="absolute bottom-0 left-0 w-full bg-black/30">
                    <div
                        className="h-1 bg-primary transition-all"
                        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                    />
                </div>
            )}
        </div>
    );
};

const FileUploadPreview: React.FC<FileUploadPreviewProps> = ({ files, onRemoveFile, progressMap }) => {
  if (files.length === 0) return null;

  return (
    <div className="p-3 border-t border-border bg-card">
      <p className="text-xs text-muted-foreground mb-2">文件预览:</p>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {files.map((file) => (
          <FilePreviewItem 
            key={`${file.name}-${file.lastModified}`} 
            file={file} 
            onRemove={onRemoveFile}
            progress={progressMap ? progressMap[file.name] : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default FileUploadPreview;
